import { createContext, useContext, useEffect, useState, useRef } from "react";
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithRedirect,
  signOut,
  signInWithPopup,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { auth, db, createUserInDatabaseIfDoesNotExist } from "./firebase";
import { getUserDisplayName } from "./components/profile/profile_utils";
import { 
  trackSignIn, 
  trackSignOut, 
  setAnalyticsUserId, 
  clearAnalyticsUserId 
} from "./utils/analytics";

const userAuth = createContext();
const provider = new GoogleAuthProvider();

export async function logIn() {
  try {
    // Set persistence to local storage
    await setPersistence(auth, browserLocalPersistence);
    
    if (process.env.REACT_APP_ENV === "development") {
      const result = await signInWithPopup(auth, provider);
      console.log("Sign in successful:", result.user.email);
      trackSignIn('google');
      return result;
    } else {
      await signInWithRedirect(auth, provider);
      // Note: trackSignIn will be called in onAuthStateChanged after redirect
    }
  } catch (error) {
    console.error("Sign in error:", error);
    throw error;
  }
}
export function logOut() {
  trackSignOut();
  clearAnalyticsUserId();
  signOut(auth);
}

export function UserAuthContextProvider({ children }) {
  const [user, setUser] = useState(null);
  const [displayName, setDisplayName] = useState(null);
  const isInitialLoad = useRef(true);
  const previousUserRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentuser) => {
      console.log("Auth state changed:", currentuser?.email || "signed out");
      const previousUser = previousUserRef.current;
      previousUserRef.current = currentuser;
      setUser(currentuser);
      
      if (currentuser) {
        try {
          // Set analytics user ID
          setAnalyticsUserId(currentuser.uid);
          
          // Track sign in if this is a new sign in (not just a page refresh)
          // Only track if: not initial load AND user changed from null to a user
          if (!isInitialLoad.current && !previousUser && currentuser) {
            trackSignIn('google');
          }
          
          // Ensure user exists in database
          await createUserInDatabaseIfDoesNotExist(db, currentuser);
          // Get display name
          const displayName = await getUserDisplayName(db, currentuser);
          setDisplayName(displayName);
        } catch (error) {
          console.error("Error setting up user:", error);
        }
      } else {
        setDisplayName(null);
        clearAnalyticsUserId();
      }
      
      // Mark initial load as complete after first auth state change
      if (isInitialLoad.current) {
        isInitialLoad.current = false;
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <userAuth.Provider
      value={{ user, logIn, logOut, displayName, setDisplayName }}
    >
      {children}
    </userAuth.Provider>
  );
}

export function useUserAuth() {
  return useContext(userAuth);
}
